import type { ShrimpState } from './types';
import { civilFromEpochDay } from './calendar';
import { held } from './held';
import { YEAR_DAYS } from './population';
import { KG_PER_POUND } from './constants';

/**
 * The deepwater shrimp (M193), *Heterocarpus laevigatus*, of the islands' slopes, "Exploitable biomass was greatest in
 * the 460-640 m depth range; negligible amounts of shrimp occurred shallower than 350m and deeper than 830m" (Ralston
 * and Tagami 1992, *Fishery Bulletin* 90: 494, p. 494; *Fishery Bulletin* is "not copyrighted", its own page): Maui Nui's
 * stock, counted by sex and month of age, growing, dying and fished day by day.
 *
 * **Its life** is Dailey and Ralston 1986's (*Fishery Bulletin* 84: 915), measured from the commercial traps' catch at
 * every main island, 1983 and 1984: von Bertalanffy's growth, "L∞ = 57.9 and 62.5 mm CL" for males
 * and females from the length frequencies and "K = 0.35 yr-1 for males and 0.25 yr-1 for females" by modal
 * progression, "preliminary" (p. 923), from age 0 (their own
 * reading: "Females mature at 40 mm CL" (p. 915), "an age of first maturity of 4 yr" (p. 923), which the curve gives
 * from age 0);
 * weight at length their regression of ln weight (g) on ln carapace length (mm), the predictive fit, for males and for
 * females without eggs (Table 3, p. 918; back-transformed without a correction, none given: open). Its natural death
 * Ralston and Tagami 1992's M/K, "Θ♀ = 1.01 ± 0.052 and Θ♂ = 0.74 ± 0.075" (p. 501), times that K (derived: their M/K
 * was fitted with their own CL∞, 58 and 50 mm, open), M 0.2525 and 0.259 a year. The young enter the traps from 16 mm
 * ("The smallest shrimp we captured in the pyramid traps was 16 mm CL") to 30 mm, fully ("CLc0 = 30 mm"), 23 mm at half
 * (p. 501): taken here as a straight ramp between, the paper's own mean of the two.
 *
 * **Its recruits** come alike every day of the year, half of them female: no year-class was seen to enter ("those of
 * females showed no coherent pattern that could be attributed to the influx of year-classes into the exploitable
 * population", Ralston and Tagami, p. 500), the sexes taken as equal at birth (the catch's ratio disagrees between the
 * two papers, 1:1.16 for females, Dailey and Ralston, p. 916, and about 60% male, Ralston and Tagami's Fig. 8: open).
 * Their number is the one fitted figure (`scripts/shrimp.ts`), so the unfished stock's exploitable weight is Maui Nui's
 * expanded estimate (`SHRIMP_MAUI_NUI_KG`). No source relates them to the spawners (open), and the shrimp's draws are
 * none: millions of shrimp, their deaths at expected values, their recruits' swing unread (open).
 */

export type ShrimpSex = 'female' | 'male';

/** The shrimp's life, each figure with its source (above). */
export const SHRIMP = {
  /** Von Bertalanffy's L∞ (mm carapace length) and K (a year), from age 0 (Dailey and Ralston 1986, p. 923). */
  linfMm: { female: 62.5, male: 57.9 },
  k: { female: 0.25, male: 0.35 },
  /** ln weight (g) = a + b ln carapace length (mm), the predictive fits (Dailey and Ralston 1986, Table 3, p. 918; females without eggs). */
  weightA: { female: -6.252, male: -6.809 },
  weightB: { female: 2.605, male: 2.755 },
  /** M/K (Ralston and Tagami 1992, p. 501). */
  mOverK: { female: 1.01, male: 0.74 },
  /** The traps' sizes: none below 16 mm, all from 30 mm (Ralston and Tagami 1992, p. 501). */
  trapFromMm: 16,
  trapFullMm: 30,
  /**
   * The oldest class, months: the last holds every shrimp of 25 years and over (no longevity read, open; at M 0.25 a
   * year, 0.2% of a year's recruits live to 25).
   */
  oldestMonths: 300,
} as const;

/**
 * Maui Nui's exploitable biomass, kg: its stratum's trapped estimate, "Maui-Lanai-Kahoolawe-Molokai", 77,457 kg over
 * 484,730 ha (Ralston and Tagami 1992, Table 2, p. 500, 1985 to 1989), expanded by the authors' own correction of their
 * traps' catchability, its Leslie estimate 9.48 ha a trap-night over the 2.4624 the catch rates' recovery gives ("we
 * obtain q=2.4624ha/trap-night", p. 502), which "would inflate biomass estimates by a factor of 3.85 (i.e., BTot =
 * 1050MT)" (p. 502), the figure NOAA's submersible survey concluded "should be accepted for management purposes as a
 * reasonable, conservative approximation" (Moffitt and Parrish 1992, *Fishery Bulletin* 90: 476, p. 481). Taken as the
 * unfished stock's: "the size-structure we observed was similar to the unexploited stock" (Ralston and Tagami, p. 498).
 */
export const SHRIMP_EXPANSION = 9.48 / 2.4624;
export const SHRIMP_MAUI_NUI_KG = 77_457 * SHRIMP_EXPANSION;

/**
 * Its fishing a year, the share of its exploitable weight taken: the main islands' landings of "both species of
 * deepwater shrimp", 22,892 lb a year on average, 2011 to 2015 (NMFS PIRO 2017, Environmental Assessment of the
 * 2016-2018 crustacean catch limits, Table 3, p. 21, a US government work; HDAR's figures), all taken as this one's (an
 * upper bound: open), over the main islands' expanded exploitable biomass, 271.4 t (Ralston and Tagami, p. 500) by the
 * same correction; the archipelago's rate taken as Maui Nui's, and no later landings read (the Council's reports
 * refused here: open).
 */
export const SHRIMP_FISHING = (22_892 * KG_PER_POUND) / (271_400 * SHRIMP_EXPANSION);

/**
 * Carapace length (mm) of a class by its months of age (von Bertalanffy's from age 0), at its shrimp's mean age over a
 * month: the classes age on each first of the month, so class a (a ≥ 1) holds shrimp a − 1 to a + 1 months old, a on
 * average, and the youngest, filling through the month, a quarter of a month (M193's reviewer: read half a month on,
 * each class entered the traps two weeks early).
 */
export function shrimpLengthMm(sex: ShrimpSex, months: number): number {
  return SHRIMP.linfMm[sex] * (1 - Math.exp(-SHRIMP.k[sex] * (months === 0 ? 0.25 : months) / 12));
}

/** Weight (g) at a carapace length (mm). */
export function shrimpWeightG(sex: ShrimpSex, mm: number): number {
  return Math.exp(SHRIMP.weightA[sex]) * mm ** SHRIMP.weightB[sex];
}

/** The share of shrimp of a length the traps take (a straight ramp, 16 to 30 mm). */
export function trapShare(mm: number): number {
  return Math.min(1, Math.max(0, (mm - SHRIMP.trapFromMm) / (SHRIMP.trapFullMm - SHRIMP.trapFromMm)));
}

/** Natural death a year (M = M/K × K). */
export const shrimpNatural = (sex: ShrimpSex): number => SHRIMP.mOverK[sex] * SHRIMP.k[sex];

const SEXES: readonly ShrimpSex[] = ['female', 'male'];

/** Each class's survival a day at a fishing rate, by sex (cached: the step's every day's). */
const survivalCache = new Map<number, Record<ShrimpSex, Float64Array>>();
function dailySurvival(fishing: number): Record<ShrimpSex, Float64Array> {
  let s = survivalCache.get(fishing);
  if (!s) {
    const of = (sex: ShrimpSex): Float64Array => Float64Array.from({ length: SHRIMP.oldestMonths + 1 }, (_, a) =>
      Math.exp(-(shrimpNatural(sex) + fishing * trapShare(shrimpLengthMm(sex, a))) / YEAR_DAYS));
    s = { female: of('female'), male: of('male') };
    survivalCache.set(fishing, s);
  }
  return s;
}

/** A sex's counts in its state. */
const countsOf = (state: ShrimpState, sex: ShrimpSex): number[] => (sex === 'female' ? state.females : state.males);

/**
 * A day of the shrimp: on the first of a month every class a month older (the oldest kept, the plus group), then each
 * class's deaths at its natural rate and the fishing on the sizes the traps take, at expected values, then the day's
 * recruits into the youngest class, half each sex; each count held to six places.
 */
export function stepShrimp(state: ShrimpState, epochDay: number, recruitsPerDay: number, fishing: number): void {
  const firstOfMonth = civilFromEpochDay(epochDay).day === 1;
  const survival = dailySurvival(fishing);
  for (const sex of SEXES) {
    const n = countsOf(state, sex);
    if (firstOfMonth) {
      const last = n.length - 1;
      n[last] = (n[last] as number) + (n[last - 1] as number);
      for (let a = last - 1; a > 0; a--) n[a] = n[a - 1] as number;
      n[0] = 0;
    }
    const s = survival[sex];
    for (let a = 0; a < n.length; a++) n[a] = held((n[a] as number) * (s[a] as number));
    n[0] = held((n[0] as number) + recruitsPerDay / 2);
  }
}

/** The shrimp's exploitable weight, kg (1,000 g, by definition): each class's weight at its length, the share the traps take. */
export function shrimpExploitableKg(state: ShrimpState): number {
  let g = 0;
  for (const sex of SEXES) {
    countsOf(state, sex).forEach((c, a) => { const mm = shrimpLengthMm(sex, a); g += c * shrimpWeightG(sex, mm) * trapShare(mm); });
  }
  return g / 1000;
}

/** An empty stock, every class none. */
export const emptyShrimp = (): ShrimpState => ({ females: new Array(SHRIMP.oldestMonths + 1).fill(0), males: new Array(SHRIMP.oldestMonths + 1).fill(0) });
